import { z } from 'zod';
import { parseWebApiResponse } from './common';
const stringOrDateSchema = z.union([z.string(), z.date()]);
const nullableStringOrDateSchema = stringOrDateSchema.nullable().optional();
const selectorItemSchema = z
    .object({
    key: z.union([z.string(), z.number()]).optional(),
    name: z.string().optional(),
    type: z.string().optional(),
    id: z.union([z.string(), z.number()]).optional(),
    portfolioId: z.union([z.string(), z.number()]).optional(),
    buildingId: z.union([z.string(), z.number()]).optional(),
    unitId: z.union([z.string(), z.number()]).optional(),
})
    .passthrough();
const audienceOperationSchema = z
    .object({
    op: z.enum(['include', 'exclude']),
    entityType: z.string(),
    entityId: z.union([z.string(), z.number()]),
    label: z.string(),
})
    .strip();
export const announcementAudienceResolveResponseSchema = z
    .object({
    status: z.enum(['resolved', 'ambiguous', 'empty', 'partial']),
    query: z.string(),
    interpretedQuery: z.string(),
    resolutionPath: z.string(),
    clarificationQuestion: z.string().nullable(),
    audiencePlan: z
        .object({
        version: z.coerce.number(),
        operations: z.array(audienceOperationSchema),
    })
        .strip(),
    preview: z
        .object({
        unitCount: z.coerce.number(),
        residentCount: z.coerce.number(),
        sampleLabels: z.array(z.string()),
    })
        .strip(),
    prefillSelection: z.array(selectorItemSchema),
    resolvedSelection: z
        .object({
        include: z.array(selectorItemSchema),
        exclude: z.array(selectorItemSchema),
    })
        .strip(),
    warnings: z.array(z.string()),
    discardedOperationCount: z.coerce.number(),
    unresolvedClauses: z.array(z.unknown()),
    ambiguities: z.array(z
        .object({
        phrase: z.string(),
        candidates: z.array(z.unknown()),
    })
        .passthrough()),
    hasIncludes: z.boolean(),
})
    .strip();
export const announcementChannelHealthResponseSchema = z
    .object({
    portfolioIds: z.array(z.coerce.number()),
    channelContext: z
        .object({
        recentAnnouncementCount1d: z.coerce.number(),
        recentAnnouncementCount7d: z.coerce.number(),
        recentAnnouncementCount30d: z.coerce.number(),
        propertyAnnouncementCounts: z.array(z.unknown()),
        lastAnnouncementSummaries: z.array(z.unknown()),
        activeConversationCount: z.coerce.number(),
        activeTier2Or3ConversationCount: z.coerce.number(),
        tierBreakdown: z.object({}).passthrough(),
        busyProperties: z.array(z.unknown()),
        scheduledAnnouncementCount7d: z.coerce.number(),
        scheduledAnnouncementCount30d: z.coerce.number(),
        calendar: z.array(z.unknown()),
    })
        .passthrough(),
})
    .strip();
export const announcementSendResponseSchema = z
    .object({
    announcementId: z.string().uuid(),
    hasOptedOut: z.union([z.string(), z.boolean()]).optional(),
    audienceWarnings: z.array(z.string()).optional(),
    outboxBatchId: z.string().uuid().nullable().optional(),
    cancelToken: z.string().uuid().optional(),
    status: z.enum(['queued']).optional(),
    queued: z.boolean().optional(),
    prepJobId: z.union([z.string(), z.number()]).nullable().optional(),
    sendAt: nullableStringOrDateSchema,
    scheduledSendId: z.coerce.number().optional(),
    scheduleGroupId: z.string().nullable().optional(),
    scheduledForUtc: nullableStringOrDateSchema,
    timezone: z.string().nullable().optional(),
    warningMessages: z.array(z.string()).optional(),
})
    .strip();
const scheduledSendDeliverySchema = z
    .object({
    id: z.coerce.number(),
    threadId: z.coerce.number(),
    unitId: z.coerce.number().nullable(),
    unitNumber: z.string().nullable(),
    recipientNames: z.array(z.string()),
    deliveryStatus: z.string(),
    threadMessageId: z.coerce.number().nullable(),
    reason: z.string().nullable(),
})
    .strip();
const scheduledSendSchema = z
    .object({
    id: z.coerce.number(),
    scheduleGroupId: z.string(),
    kind: z.string(),
    status: z.string(),
    accountId: z.coerce.number().nullable().optional(),
    portfolioId: z.coerce.number().nullable().optional(),
    propertyName: z.string().nullable().optional(),
    threadId: z.coerce.number().nullable().optional(),
    createdByUserId: z.coerce.number().nullable().optional(),
    updatedByUserId: z.coerce.number().nullable().optional(),
    timezone: z.string(),
    scheduledForUtc: stringOrDateSchema,
    scheduledForLocalDate: z.string(),
    scheduledForLocalTime: z.string(),
    previewText: z.string().nullable().optional(),
    mediaUrl: z.string().nullable().optional(),
    deliveryCount: z.coerce.number().nullable().optional(),
    sentCount: z.coerce.number().nullable().optional(),
    failedCount: z.coerce.number().nullable().optional(),
    skippedCount: z.coerce.number().nullable().optional(),
    sentAt: nullableStringOrDateSchema,
    canceledAt: nullableStringOrDateSchema,
    createdAt: nullableStringOrDateSchema,
    updatedAt: nullableStringOrDateSchema,
    scheduledForLabel: z.string(),
    warningMessages: z.array(z.string()),
    groupSize: z.coerce.number(),
    deliveries: z.array(scheduledSendDeliverySchema).optional(),
})
    .strip();
export const scheduledSendsListResponseSchema = z
    .object({
    scheduledSends: z.array(scheduledSendSchema),
})
    .strip();
export const scheduledSendDetailResponseSchema = z
    .object({
    scheduledSend: scheduledSendSchema,
    group: z.array(scheduledSendSchema),
})
    .strip();
export const scheduledSendMutationResponseSchema = z.object({}).passthrough();
export function parseAnnouncementAudienceResolveResponse(data) {
    return parseWebApiResponse(announcementAudienceResolveResponseSchema, data, 'POST /api/announcements/audience/resolve');
}
export function parseAnnouncementChannelHealthResponse(data) {
    return parseWebApiResponse(announcementChannelHealthResponseSchema, data, 'GET /api/announcements/channel-health');
}
export function parseAnnouncementSendResponse(data) {
    return parseWebApiResponse(announcementSendResponseSchema, data, 'POST /api/announcements');
}
export function parseScheduledSendsListResponse(data) {
    return parseWebApiResponse(scheduledSendsListResponseSchema, data, 'GET /api/scheduled-sends');
}
export function parseScheduledSendDetailResponse(data) {
    return parseWebApiResponse(scheduledSendDetailResponseSchema, data, 'GET /api/scheduled-sends/:id');
}
